import {
  GetProductAttribute,
  GetOrderTypeProductDetailBatch,
  GetProductAttributeBatch,
  SkuComponentGetList,
  SkuGet
} from '@/api/Sku.ts'
import { useStoreLogger } from '@/logger.ts'
import { GetImageRootUrl, GetProductRootUrl } from '@/stores/SettingsStore.ts'
import { GetCacheItem, SetCacheItem } from '@/services/CacheService.ts'
import type {
  IProductAttributeGetResponse,
  IProductExWithSkuExsViewModel,
  IAOEProductAttributeGetResponse,
  ISkuComponentItem,
  ISkuDetail,
  ISkuNestedImage
} from '@/types/SkuTypes.ts'
import logger from '../logger'

export async function GetFullProductImageUrl(
  productCode: string,
  relativeUrl: string,
) {
  const logger = useStoreLogger()
  const imageUrl =
    (await GetImageRootUrl()) + '/catalogcontent/' + encodeURI(relativeUrl)
  logger.debug(
    'GetFullProductImageUrl',
    productCode,
    relativeUrl,
    'url=',
    imageUrl,
  )
  return imageUrl
}

export async function GetFullProductUrl(
  categoryCode: string,
  productCode: string,
) {
  const logger = useStoreLogger()
  const productUrl =
    (await GetProductRootUrl()) + '/' + categoryCode + '/' + productCode
  logger.debug(
    'GetFullProductUrl',
    categoryCode,
    productCode,
    'url=',
    productUrl,
  )
  return productUrl
}

export async function GetFullProductImageUrlCached(productCode: string) {
  const logger = useStoreLogger()
  logger.debug('GetFullProductImageUrlCached', productCode)
  const relativeUrl = await GetProductImageRelativeUrl(productCode)
  return await GetFullProductImageUrl(productCode, relativeUrl)
}

async function GetProductImageRelativeUrl(
  productCode: string,
): Promise<string> {
  const productAttribute: IProductAttributeGetResponse =
    await GetProductAttribute({ productCode, attributeName: 'CatalogImage' })
  return productAttribute.item.attributeValue
}

export async function GetFullProductImageDictionary(
  productCodes: Array<string>,
): Promise<Record<string, string>> {
  const logger = useStoreLogger()
  logger.debug('GetFullProductImageDictionary', productCodes)
  const productImageUrls: Record<string, string> = {}

  // Skip if no product codes
  if (!productCodes || productCodes.length === 0) {
    return productImageUrls
  }

  const productAttributes = await GetCachedProductAttributes(
    productCodes,
    'CatalogImage',
  )

  logger.debug('productAttributes=', productAttributes)

  if (productAttributes && Array.isArray(productAttributes)) {
    // Process all attributes in parallel for better performance
    const imagePromises = productAttributes.map(async (productAttribute) => {
      if (productAttribute && productAttribute.item) {
        const { productCode, attributeValue } = productAttribute.item
        if (productCode && attributeValue) {
          const imageUrl = await GetFullProductImageUrl(productCode, attributeValue)
          return { productCode, imageUrl }
        }
      }
      return null
    })

    // Wait for all image URLs to be resolved
    const results = await Promise.all(imagePromises)

    // Add all valid results to the dictionary
    results.forEach(result => {
      if (result) {
        productImageUrls[result.productCode] = result.imageUrl
      }
    })
  }

  logger.debug('productImageUrls=', productImageUrls)
  return productImageUrls
}

export async function GetCachedProductDetails(
  orderTypeId: string,
  productCodes: Array<string>,
): Promise<Array<IProductExWithSkuExsViewModel>> {
  const logger = useStoreLogger()
  logger.debug('GetCachedProductDetails', orderTypeId, productCodes)
  const productDetails: Array<IProductExWithSkuExsViewModel> = []
  const productCodesNotCached: Array<string> = []
  for (const productCode of productCodes) {
    const cacheItem = GetCacheItem<IProductExWithSkuExsViewModel>(
      `PD_${orderTypeId}_${productCode}`,
    )
    if (cacheItem) {
      productDetails.push(cacheItem)
    } else {
      productCodesNotCached.push(productCode)
    }
  }
  if (productCodesNotCached.length > 0) {
    const productDetailsBatch = await GetProductDetailsBatch(
      orderTypeId,
      productCodesNotCached,
    )
    if (productDetailsBatch) {
      for (const productDetail of productDetailsBatch) {
        const cacheKey = `PD_${orderTypeId}_${productDetail.productCode}`
        SetCacheItem(cacheKey, productDetail, 60 * 60)
        productDetails.push(productDetail)
      }
    }
  }
  return productDetails
}

const GaranNestedImageUrlAttributeName = 'GARANnestedimageurl'
const GaranImageUrlAttributeName = 'GARANimageurl'

// Attribute keys are matched case-insensitively since their casing isn't guaranteed.
function GetAttributeString(
  attributes: Record<string, unknown>,
  attributeName: string,
): string {
  const key = Object.keys(attributes).find(
    k => k.toLowerCase() === attributeName.toLowerCase(),
  )
  const value = key ? attributes[key] : null
  return typeof value === 'string' ? value.trim() : ''
}

// Maps skuCode to its nested/full image urls; only SKUs with both urls populated are included.
export function GetSkuNestedImagesBySkuCode(
  productDetails: Array<IProductExWithSkuExsViewModel>,
): Record<string, ISkuNestedImage> {
  const nestedImagesBySkuCode: Record<string, ISkuNestedImage> = {}
  for (const productDetail of productDetails) {
    for (const sku of productDetail.skus ?? []) {
      if (!sku.attributes) continue
      const nestedImageUrl = GetAttributeString(
        sku.attributes,
        GaranNestedImageUrlAttributeName,
      )
      const imageUrl = GetAttributeString(
        sku.attributes,
        GaranImageUrlAttributeName,
      )
      if (nestedImageUrl && imageUrl) {
        nestedImagesBySkuCode[sku.skuCode] = { nestedImageUrl, imageUrl }
      }
    }
  }
  return nestedImagesBySkuCode
}

export async function GetProductDetailsBatch(
  orderTypeId: string,
  productCodes: Array<string>,
  includeCrossSells: boolean = false,
): Promise<Array<IProductExWithSkuExsViewModel>> {
  const logger = useStoreLogger()
  logger.debug('GetOrderTypeProductDetailBatch', orderTypeId, productCodes)
  const productDetails: Array<IProductExWithSkuExsViewModel> = []
  const responses = await GetOrderTypeProductDetailBatch(
    orderTypeId,
    productCodes,
    includeCrossSells,
  )
  if (responses) {
    for (const response of responses) {
      productDetails.push(response.item)
    }
  }
  return productDetails
}

export async function GetCachedProductAttributes(
  productCodes: Array<string>,
  attributename: string,
): Promise<Array<IAOEProductAttributeGetResponse> | null> {
  const storeLogger = useStoreLogger()
  storeLogger.debug(
    'GetCachedProductAttributes',
    'attributename=',
    attributename,
    'productCodes.length=',
    productCodes?.length ?? 0,
    'productCodes.preview=',
    (productCodes ?? []).slice(0, 10),
  )
  // Return early if no product codes
  if (!productCodes || productCodes.length === 0) {
    return []
  }

  const result: Array<IAOEProductAttributeGetResponse> = []
  const productCodesNotCache: Array<string> = []

  // Check cache first for each product code
  for (const productCode of productCodes) {
    const cacheItem = GetCacheItem<IAOEProductAttributeGetResponse>(
      `PA_${productCode}_${attributename}`,
    )
    if (cacheItem) {
      result.push(cacheItem)
    } else {
      productCodesNotCache.push(productCode)
    }
  }

  // If we have product codes not in cache, fetch them
  if (productCodesNotCache.length > 0) {
    try {
      // Try to fetch all at once
      const productAttributes = await GetProductAttributes(productCodesNotCache, attributename)
      if (productAttributes && Array.isArray(productAttributes)) {
        result.push(...productAttributes)
      }
    } catch (error) {
      // If batch request fails, try individual requests
      logger.error('Error getting product attributes in batch:', error)

      // Process individual requests in parallel for better performance
      const individualPromises = productCodesNotCache.map(async (productCode) => {
        try {
          const singleProd: Array<string> = [productCode]
          const productAttributes = await GetProductAttributes(singleProd, attributename)
          return productAttributes && Array.isArray(productAttributes) ? productAttributes : null
        } catch (error) {
          logger.error(`Error getting product attribute for ${productCode}:`, error)
          return null
        }
      })

      // Wait for all individual requests to complete
      const results = await Promise.all(individualPromises)

      // Add successful results to the main result array
      results.forEach(attributes => {
        if (attributes && Array.isArray(attributes)) {
          result.push(...attributes)
        }
      })
    }
  }

  return result
}

async function GetProductAttributes(productCodesNotCache: Array<string>, attributename: string): Promise<Array<IAOEProductAttributeGetResponse> | null> {
  const productAttributes = await GetProductAttributeBatch(
    productCodesNotCache,
    attributename,
  )

  if (productAttributes && Array.isArray(productAttributes)) {
    // Cache each product attribute
    for (const productAttribute of productAttributes) {
      if (productAttribute && productAttribute.item) {
        const cacheKey = `PA_${productAttribute.item.productCode}_${attributename}`
        SetCacheItem(cacheKey, productAttribute, 60 * 60)
      }
    }
    return productAttributes
  }

  return null
}

export async function GetCachedSkuComponents(
  skuCode: string
): Promise<Array<ISkuComponentItem> | null> {
  const logger = useStoreLogger()
  logger.debug('GetCachedSkuComponents', skuCode)

  // Check cache first
  const cacheKey = `SC_${skuCode}`
  const cacheItem = GetCacheItem<Array<ISkuComponentItem>>(cacheKey)

  if (cacheItem) {
    return cacheItem
  }

  // If not in cache, fetch from API
  try {
    const response = await SkuComponentGetList(skuCode)
    if (response?.items) {
      // Cache the result for 1 hour
      SetCacheItem(cacheKey, response.items, 60 * 60)
      return response.items
    }
  } catch (error) {
    logger.error('Error getting SKU components:', error)
  }

  return null
}

export async function GetCachedSkuDetail(
  skuCode: string
): Promise<ISkuDetail | null> {
  const logger = useStoreLogger()
  logger.debug('GetCachedSkuDetail', skuCode)

  // Check cache first
  const cacheKey = `SD_${skuCode}`
  const cacheItem = GetCacheItem<ISkuDetail>(cacheKey)

  if (cacheItem) {
    return cacheItem
  }

  // If not in cache, fetch from API
  try {
    const response = await SkuGet(skuCode)
    if (response?.item) {
      // Cache the result for 1 hour
      SetCacheItem(cacheKey, response.item, 60 * 60)
      return response.item
    }
  } catch (error) {
    logger.error('Error getting SKU detail:', error)
  }

  return null
}

export async function GetIsOnlyAvailableForHost(
  orderTypeId: string,
  skuCode: string,
): Promise<boolean> {
  const logger = useStoreLogger()
  logger.debug('GetIsOnlyAvailableForHost', orderTypeId, skuCode)

  const productDetails = await GetCachedProductDetails(orderTypeId, [skuCode])
  if (!productDetails || productDetails.length === 0 || !productDetails[0].skus?.length) {
    return false
  }
  return productDetails[0].skus[0].onlyAvailableForHost === true
}

export async function GetIsOnlyAvailableForDiscount(
  orderTypeId: string,
  skuCode: string,
): Promise<boolean> {
  const logger = useStoreLogger()
  logger.debug('GetIsOnlyAvailableForDiscount', orderTypeId, skuCode)

  const productDetails = await GetCachedProductDetails(orderTypeId, [skuCode])
  if (!productDetails || productDetails.length === 0 || !productDetails[0].skus?.length) {
    return false
  }
  return productDetails[0].skus[0].onlyAvailableForDiscount === true
}

export async function GetSkuComponentsWithDetails(
  skuCode: string
): Promise<Array<{component: ISkuComponentItem, details: ISkuDetail | null}> | null> {
  const logger = useStoreLogger()
  logger.debug('GetSkuComponentsWithDetails', skuCode)

  const components = await GetCachedSkuComponents(skuCode)

  if (!components || components.length === 0) {
    return null
  }

  const result: Array<{component: ISkuComponentItem, details: ISkuDetail | null}> = []

  // For each component, get its details
  for (const component of components) {
    const details = await GetCachedSkuDetail(component.skuCodeChild)
    result.push({
      component,
      details
    })
  }

  return result
}
